<template>
  <div class="video-attachment" ref="rootEl">
    <div class="video-attachment__wrapper">
      <div class="video-attachment__container">
        <div v-if="!videoLoaded" class="video-attachment__placeholder" @click="loadVideo">
          <div v-if="isLoading" class="video-attachment__progress-container">
            <ProgressCircle
              v-if="loadingProgress > 0"
              :progress="loadingProgress"
              size="medium"
              bg-stroke="rgba(255, 255, 255, 0.2)"
              fill-stroke="white"
            />
            <Icon
              v-else
              icon="mdi:loading"
              class="video-attachment__preview-icon video-attachment__preview-icon--spin"
            />
          </div>
          <div
            v-else-if="loadError"
            class="video-attachment__icon-container video-attachment__icon-container--error"
          >
            <Icon icon="mdi:refresh" class="video-attachment__preview-icon" />
          </div>
          <div v-else class="video-attachment__icon-container">
            <Icon icon="mdi:play-circle-outline" class="video-attachment__preview-icon" />
          </div>
          <span v-if="isLoading && loadingProgress > 0">{{ Math.round(loadingProgress) }}%</span>
          <span v-else-if="isLoading && loadedBytes > 0"
            >{{ formatFileSize(loadedBytes) }} downloaded</span
          >
          <span v-else-if="isLoading">Loading...</span>
          <span v-else-if="loadError">Failed — tap to retry</span>
          <span v-else>Tap to play</span>
        </div>
        <video
          v-else-if="resolvedVideoUrl"
          :src="resolvedVideoUrl"
          controls
          autoplay
          muted
          playsinline
          :loop="!!attachment.gif"
          class="video-attachment__player"
        >
          Your browser does not support the video tag.
        </video>
        <button
          v-if="videoLoaded && resolvedVideoUrl"
          class="video-attachment__download"
          @click.stop="handleDownload"
          :disabled="isDownloading"
          title="Download video"
        >
          <ProgressCircle
            v-if="isDownloading && downloadProgress > 0"
            :progress="downloadProgress"
            size="small"
            bg-stroke="rgba(255, 255, 255, 0.3)"
            fill-stroke="white"
            :show-text="false"
          />
          <Icon v-else-if="isDownloading" icon="mdi:loading" class="video-attachment__spinner" />
          <Icon v-else icon="mdi:download" />
        </button>
        <div v-if="attachment.gif" class="video-attachment__badge video-attachment__badge--gif">
          <span>GIF</span>
        </div>
        <div v-if="attachment.duration" class="video-attachment__duration">
          <Icon icon="mdi:play-circle" />
          <span>{{ formatDuration(attachment.duration) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentLoader } from "@/composables/attachments/useAttachmentLoader";
import { useAttachmentDownload } from "@/composables/attachments/useAttachmentDownload";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}

const props = defineProps<Props>();

const rootEl = ref<HTMLElement | null>(null);
const videoLoaded = ref(false);
const loadError = ref(false);

const {
  isLoading,
  loadingProgress,
  loadedBytes,
  blobUrl,
  loadAttachment,
  getAttachmentUrl,
  error,
  cleanup,
} = useAttachmentLoader(props.attachment, props.messageId, () => {});

const {
  isDownloading,
  loadingProgress: downloadProgress,
  download,
} = useAttachmentDownload(props.attachment, props.messageId);

const resolvedVideoUrl = computed(() => {
  if (blobUrl.value) return blobUrl.value;
  return getAttachmentUrl(props.attachment.url);
});

const loadVideo = async () => {
  if (isLoading.value) return;
  loadError.value = false;
  const blob = await loadAttachment("video/mp4");
  if (blob) {
    videoLoaded.value = true;
  } else if (error.value) {
    loadError.value = true;
  }
  // null without error.value = aborted (unmount/retry) — do nothing
};

const handleDownload = () => {
  download("video/mp4", `video_${props.attachment.id}`);
};

watch(
  () => resolvedVideoUrl.value,
  (url) => {
    const attachmentUrl = props.attachment.url;
    if (url && attachmentUrl && !attachmentUrl.startsWith("att://")) {
      videoLoaded.value = true;
    }
  },
  { immediate: true }
);

let _observer: IntersectionObserver | null = null;

onMounted(() => {
  const url = props.attachment.url;
  if (!url || url.startsWith("att://")) {
    const el = rootEl.value;
    if (!el) {
      loadVideo();
      return;
    }
    _observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          _observer?.disconnect();
          _observer = null;
          loadVideo();
        }
      },
      { threshold: 0.1 }
    );
    _observer.observe(el);
  }
});

onUnmounted(() => {
  _observer?.disconnect();
  _observer = null;
  cleanup();
});

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};
</script>

<style scoped>
.video-attachment {
  position: relative;
  max-width: 300px;
}

.video-attachment__wrapper {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  background: #000;
}

.video-attachment__wrapper:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.video-attachment__container {
  position: relative;
}

.video-attachment__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #d1d5db;
  color: #6b7280;
  font-size: 0.875rem;
  min-height: 120px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.video-attachment__placeholder:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #111827;
}

.video-attachment__progress-container {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-attachment__icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.video-attachment__placeholder:hover .video-attachment__icon-container {
  background: rgba(0, 0, 0, 0.7);
  transform: scale(1.05);
}

.video-attachment__icon-container--error {
  background: rgba(185, 28, 28, 0.5);
}

.video-attachment__preview-icon--spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.video-attachment__preview-icon {
  font-size: 1.5rem;
  color: #ffffff;
  transition: all 0.2s ease;
}

.video-attachment__placeholder:hover .video-attachment__preview-icon {
  transform: translateY(2px);
}

.video-attachment__player {
  width: 100%;
  max-height: 400px;
  display: block;
  border-radius: 12px;
}

.video-attachment__download {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
  position: relative;
}

.video-attachment__download:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.05);
}

.video-attachment__download:active:not(:disabled) {
  transform: scale(0.95);
}

.video-attachment__download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.video-attachment__spinner {
  font-size: 1.25rem;
  animation: spin 1s linear infinite;
}

.video-attachment__badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: white;
  border-radius: 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.video-attachment__badge--gif {
  background: rgba(0, 0, 0, 0.75);
}

.video-attachment__duration {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: white;
  border-radius: 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>
